import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { listRoutes } from "../../api/routes";
import { getCityDisplay } from "../../utils/city";

const RoutesListPage = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError("");
      setLoading(true);
      try {
        const rows = await listRoutes();
        if (!cancelled) setRoutes(rows);
      } catch {
        if (!cancelled) {
          setError(t("admin.routesLoadError"));
          setRoutes([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const stopsLabel = (route) =>
    (route.stops || [])
      .map((stop) => getCityDisplay(stop.city))
      .filter(Boolean)
      .join(" → ");

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {t("admin.nav.routes")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("admin.routesHint")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/administrator/routes/new")}
          sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
        >
          {t("admin.routesNew")}
        </Button>
      </Stack>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.route")}</TableCell>
              <TableCell>{t("admin.routesStops")}</TableCell>
              <TableCell>{t("admin.status")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3}>{t("admin.loading")}</TableCell>
              </TableRow>
            ) : null}
            {!loading && routes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography color="text.secondary">{t("admin.routesEmpty")}</Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {routes.map((route) => (
              <TableRow
                key={route.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(`/administrator/routes/${route.id}`)}
              >
                <TableCell>
                  <Typography
                    component={RouterLink}
                    to={`/administrator/routes/${route.id}`}
                    color="inherit"
                    fontWeight={600}
                    sx={{ textDecoration: "none" }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {route.name}
                  </Typography>
                </TableCell>
                <TableCell>{stopsLabel(route) || "—"}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={
                      route.active === false
                        ? t("admin.routesInactive")
                        : t("admin.routesActive")
                    }
                    color={route.active === false ? "default" : "success"}
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RoutesListPage;
